/* sugar:assets/sugar-animated-icon.js@2.0.0 */
/* <sugar-animated-icon src="…"> — inline-SVG renderer for the icon CDN's
 * inline families (lmd-*, lottie-*, sugar-*). SMIL animations only run when the
 * SVG is inline in the DOM: as an <img> or CSS mask the browser freezes them at
 * t=0, and draw-in icons are blank at t=0 (validated 2026-08-20, Chrome).
 * So this element fetches the SVG text (CDN serves ACAO: *) and injects it.
 * Inline also means currentColor recoloring works, same as the mask family.
 * Fetch failure hides the element — parity with the mask snippet's fallback.
 * SMIL ignores prefers-reduced-motion, so when the visitor asks for less motion
 * the injected drawing is paused and jumped to its end state (a looping icon
 * shows one still frame).
 */

const cache = new Map(); // url -> Promise<string>
const reduceMotion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

function settle(svg) {
  if (!svg || !reduceMotion || !reduceMotion.matches || !svg.pauseAnimations) return;
  svg.pauseAnimations();
  svg.setCurrentTime(60); // past any draw-in; fill="freeze" holds the final frame
}

function fetchSvg(url) {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`icon ${r.status}`);
      return r.text();
    });
    p.catch(() => cache.delete(url)); // don't cache failures
    cache.set(url, p);
  }
  return p;
}

class SugarAnimatedIcon extends HTMLElement {
  async connectedCallback() {
    const src = this.getAttribute("src");
    if (!src || this._loaded === src) return;
    try {
      const text = await fetchSvg(src);
      if (!this.isConnected || this.getAttribute("src") !== src) return;
      this.innerHTML = text; // fresh subtree per instance restarts SMIL
      this._loaded = src;
      settle(this.querySelector("svg"));
    } catch {
      this.style.display = "none";
    }
  }

  /* Re-inject to replay the animation from t=0. */
  replay() {
    this._loaded = null;
    this.connectedCallback();
  }
}

if (!customElements.get("sugar-animated-icon")) {
  customElements.define("sugar-animated-icon", SugarAnimatedIcon);
}
